import {
  X,
  Download,
  Columns2,
  Search,
  Copy,
  SlidersHorizontal,
  Sun,
  Moon,
  Heart,
  LayoutGrid,
  List,
  CodeXml,
  GripVertical,
  ArrowUpRight,
  CircleHelp,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronUp,
  ChevronDown,
  Coffee,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { convert } from "./converter.js";
import { translations } from "./translations.js";
import { mapPreviewText, resolvePreviewMapping } from "./preview-mapping.js";
import mappingRegistry from "../dist/font-mappings.json";
export const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);
export const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};
const loading = new Map();
const loadedFonts = new Set();
export function loadFont(f) {
  if (!f?.file || f.previewUnsupported) return Promise.resolve(false);
  if (!loading.has(f.id))
    loading.set(
      f.id,
      (async () => {
        try {
          const face = new FontFace(
            "font" + f.id,
            `url("${f.previewFile || f.file}")`,
          );
          await face.load();
          document.fonts.add(face);
          loadedFonts.add(f.id);
          return true;
        } catch {
          return false;
        }
      })(),
    );
  return loading.get(f.id);
}
export function encode(f, text, mapping) {
  return mapPreviewText(text, f, { mapping });
}
export const eligible = (f) =>
  f.file &&
  !f.previewUnsupported &&
  !f.embeddingFlags?.restrictedLicense &&
  !f.embeddingFlags?.bitmapOnly;
export function FontText({
  font,
  text,
  as: Tag = "div",
  mapping,
  style,
  ...props
}) {
  const app = useApp();
  const autoConvert = app?.autoConvert !== false;
  const [result, setResult] = useState(null);
  const loaded = loadedFonts.has(font.id);
  const failed = result?.id === font.id && !result.ok;
  const pending = !loaded && !failed && !font.previewUnsupported && !!font.file;
  const fallbackCharacters = autoConvert && mappingRegistry.fonts[font.id]?.fallbackCharacters;
  useEffect(() => {
    let live = true;
    loadFont(font).then((ok) => live && setResult({id:font.id, ok}));
    return () => {
      live = false;
    };
  }, [font?.id]);
  return (
    <Tag
      {...props}
      data-preview={font.id}
      data-loaded={loaded}
      data-font-loading={pending || undefined}
      aria-busy={pending || undefined}
      data-auto-convert={autoConvert}
      data-mapping={resolvePreviewMapping(font, mapping).label}
      style={{
        ...style,
        fontFamily: loaded
          ? `font${font.id},InterfaceGeo,sans-serif`
          : undefined,
      }}
    >
      {font.previewUnsupported || failed || !font.file
        ? "Browser preview unavailable · Download original font"
        : fallbackCharacters && !mapping
          ? [...text].map((char, i) => fallbackCharacters.includes(char)
            ? <span key={i} style={{fontFamily:"InterfaceGeo,sans-serif"}}>{char}</span>
            : mapPreviewText(char, font, { mapping, enabled: autoConvert }))
          : mapPreviewText(text, font, { mapping, enabled: autoConvert })}
    </Tag>
  );
}
export function AutoConvertToggle() {
  const a = useApp(),
    t = useTranslate();
  return (
    <button
      className={"auto-convert-toggle " + (a.autoConvert ? "enabled" : "")}
      type="button"
      role="switch"
      aria-checked={a.autoConvert}
      aria-label="Auto convert preview text"
      title={t(
        "Keep your Unicode text and adapt it to each font.",
        "Unicode ტექსტი შენარჩუნდება და თითოეულ შრიფტს მოერგება.",
      )}
      onClick={() => a.setAutoConvert(!a.autoConvert)}
    >
      <span className="auto-convert-track">
        <i />
      </span>
      <span>
        {t("Auto convert", "ავტოკონვერტაცია")}:{" "}
        <b>{a.autoConvert ? t("On", "ჩართ.") : t("Off", "გამორთ.")}</b>
      </span>
    </button>
  );
}
const typingModes = new Map();
export function TypingInput({
  id,
  value,
  onChange,
  multiline = false,
  ...props
}) {
  const [mode, setMode] = useState(typingModes.get(id) || "en");
  const ref = useRef(null);
  const Tag = multiline ? "textarea" : "input";
  useEffect(() => {
    const el = ref.current;
    const handle = (e) => {
      if (
        mode !== "ka" ||
        e.isComposing ||
        e.inputType !== "insertText" ||
        !e.data ||
        !e.cancelable
      )
        return;
      const next = convert(e.data, "_eng_", "_utf8_");
      if (next === e.data) return;
      e.preventDefault();
      const start = el.selectionStart,
        end = el.selectionEnd;
      onChange(value.slice(0, start) + next + value.slice(end));
      requestAnimationFrame(() =>
        el.setSelectionRange(start + next.length, start + next.length),
      );
    };
    el.addEventListener("beforeinput", handle);
    return () => el.removeEventListener("beforeinput", handle);
  }, [mode, value, onChange]);
  return (
    <div className="typing-field">
      <Tag
        {...props}
        id={id}
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="typing-toggle" role="group" aria-label="Typing language">
        {[
          ["ka", "ქართული"],
          ["en", "English"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            data-typing-for={id}
            data-typing-mode={k}
            aria-pressed={mode === k}
            onClick={() => {
              typingModes.set(id, k);
              setMode(k);
              ref.current.focus();
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
export function useTranslate() {
  const { lang } = useApp();
  return (en, ka) => (lang === "ka" ? ka || translations[en] || en : en);
}
export function Icon({ name, ...props }) {
  const icons = {
    coffee: Coffee,
    up: ChevronUp,
    down: ChevronDown,
    collapse: PanelLeftClose,
    expand: PanelLeftOpen,
    close: X,
    download: Download,
    compare: Columns2,
    search: Search,
    copy: Copy,
    filter: SlidersHorizontal,
    sun: Sun,
    moon: Moon,
    heart: Heart,
    grid: LayoutGrid,
    list: List,
    code: CodeXml,
    grip: GripVertical,
    external: ArrowUpRight,
    help: CircleHelp,
  };
  const Glyph = icons[name] || CircleHelp;
  return <Glyph size={20} strokeWidth={1.8} aria-hidden="true" {...props} />;
}
export function Modal({ children, close, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    el.showModal();
    return () => el.close();
  }, []);
  return (
    <dialog
      id="detail"
      className={className}
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          const r = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            close();
        }
      }}
    >
      {children}
    </dialog>
  );
}
export function Close({ onClick, label = "Close" }) {
  return (
    <button
      className="iconbutton"
      data-action="close"
      aria-label={label}
      onClick={onClick}
    >
      <Icon name="close" />
    </button>
  );
}
export const presets = {
  alphabet: ["Alphabet", "აბგდევზთიკლმნოპჟრსტუფქღყშჩცძწჭხჯჰ"],
  pangram: ["Georgian pangram", "ჭკვიანი ჟღალი მელა ზარმაც ძაღლს გადაახტა. ბუჩქთან ვეფხვი წევს, წყალში კი ჰიპოპოტამი ჯოხს ეძებს."],
  rustaveli: ["Rustaveli", "რომელმან შექმნა სამყარო ძალითა მით ძლიერითა"],
  everyday: ["Everyday text", "გამარჯობა! როგორ ხარ? დღეს მშვენიერი დღეა."],
  numbers: ["Numbers", "0123456789 ₾ € $ % + − = ( ) ! ?"],
  custom: ["Custom", ""],
};
